/**
 * Webtech HR Engine™ - Dashboard Employé
 * Un coach personnel de progression, pas un outil de contrôle.
 * Design simple, rassurant et motivant.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import api from '../lib/api';
import { 
  User,
  Building2,
  Target,
  CheckCircle2,
  Clock,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  ArrowRight,
  Star,
  Award,
  Brain,
  FileText,
  MessageSquare,
  Calendar,
  Sparkles,
  ChevronRight
} from 'lucide-react';

// Composant Badge de statut
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'en_progression': { 
      label: 'En progression', 
      color: 'bg-green-100 text-green-700 border-green-200',
      icon: TrendingUp 
    },
    'a_ameliorer': { 
      label: 'À améliorer', 
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: AlertCircle 
    },
    'action_requise': { 
      label: 'Action requise', 
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: Clock 
    },
    'stable': { 
      label: 'Stable', 
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: CheckCircle2 
    }
  };
  
  const config = statusConfig[status] || statusConfig['stable'];
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} border px-3 py-1 text-sm font-medium`}>
      <Icon className="w-3.5 h-3.5 mr-1.5" />
      {config.label}
    </Badge>
  );
};

// Composant Carte d'action
const ActionCard = ({ action, onAction }) => {
  const iconMap = {
    'auto_evaluation': FileText,
    'validate_objectives': Target,
    'give_feedback': MessageSquare,
    'complete_skills': Brain,
    'default': CheckCircle2
  };
  
  const Icon = iconMap[action.type] || iconMap['default'];
  
  const priorityColors = {
    'high': 'border-l-red-500',
    'medium': 'border-l-amber-500',
    'low': 'border-l-blue-500'
  };
  
  return (
    <div className={`bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-all border-l-4 ${priorityColors[action.priority] || 'border-l-slate-300'}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-slate-900">{action.title}</h4>
          <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
          {action.deadline && (
            <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
              <Calendar className="w-3 h-3" />
              <span>Avant le {new Date(action.deadline).toLocaleDateString('fr-FR')}</span>
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => onAction(action)} className="flex-shrink-0">
          Faire
          <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Composant Objectif
const ObjectiveCard = ({ objective }) => {
  const statusColors = {
    'pending': 'text-amber-600 bg-amber-50',
    'en_cours': 'text-blue-600 bg-blue-50',
    'en_retard': 'text-red-600 bg-red-50',
    'atteint': 'text-green-600 bg-green-50'
  };
  
  const statusLabels = {
    'pending': 'À évaluer',
    'en_cours': 'Auto-évalué',
    'en_retard': 'En retard',
    'atteint': 'Complété'
  };
  
  // Use selfScore if available, otherwise use progress
  const displayProgress = objective.selfScore || objective.progress || 0;
  const displayStatus = objective.selfEvalDone ? 
    (objective.managerEvalDone ? 'atteint' : 'en_cours') : 
    'pending';
  
  return (
    <div className={`bg-white rounded-lg border p-4 hover:shadow-sm transition-all ${
      objective.selfEvalDone ? 'border-blue-200' : 'border-slate-200'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {objective.selfEvalDone && (
              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
            )}
            <h4 className="text-sm font-medium text-slate-900 truncate">{objective.title}</h4>
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">
                {objective.selfEvalDone ? 'Score auto-éval' : 'Progression'}
              </span>
              <span className="font-medium text-slate-700">{displayProgress}%</span>
            </div>
            <Progress value={displayProgress} className="h-2" />
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[displayStatus] || statusColors['pending']}`}>
              {statusLabels[displayStatus] || 'À évaluer'}
            </span>
            <span className="text-xs text-slate-400">
              Poids: {objective.weight || 20}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Radar simple pour compétences
const SimpleRadarChart = ({ skills }) => {
  if (!skills || skills.length === 0) return null;
  
  const maxLevel = 5;
  const centerX = 100;
  const centerY = 100;
  const radius = 70;
  
  const angleStep = (2 * Math.PI) / skills.length;
  
  // Points du polygone
  const points = skills.map((skill, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const r = (skill.level / maxLevel) * radius;
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle)
    };
  });
  
  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  // Grille
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1];
  
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[250px] mx-auto">
      {/* Grille */}
      {gridLevels.map((level, i) => (
        <polygon
          key={i}
          points={skills.map((_, j) => {
            const angle = j * angleStep - Math.PI / 2;
            const r = level * radius;
            return `${centerX + r * Math.cos(angle)},${centerY + r * Math.sin(angle)}`;
          }).join(' ')}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      
      {/* Lignes des axes */}
      {skills.map((_, i) => {
        const angle = i * angleStep - Math.PI / 2;
        return (
          <line
            key={i}
            x1={centerX}
            y1={centerY}
            x2={centerX + radius * Math.cos(angle)}
            y2={centerY + radius * Math.sin(angle)}
            stroke="#e2e8f0"
            strokeWidth="1"
          />
        );
      })}
      
      {/* Polygone des valeurs */}
      <polygon
        points={polygonPoints}
        fill="rgba(37, 99, 235, 0.2)"
        stroke="#2563eb"
        strokeWidth="2"
      />
      
      {/* Points */}
      {points.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill={skills[i].level >= 3 ? '#22c55e' : skills[i].level >= 2 ? '#f59e0b' : '#ef4444'}
          stroke="white"
          strokeWidth="2"
        />
      ))}
      
      {/* Labels */}
      {skills.map((skill, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const labelRadius = radius + 25;
        const x = centerX + labelRadius * Math.cos(angle);
        const y = centerY + labelRadius * Math.sin(angle);
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-[9px] fill-slate-600"
          >
            {skill.name.length > 12 ? skill.name.substring(0, 12) + '...' : skill.name}
          </text>
        );
      })}
    </svg>
  );
};

export default function EmployeeDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, isFree, loading: planLoading } = usePlan();
  
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [pendingActions, setPendingActions] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [skills, setSkills] = useState([]);
  const [skillProfile, setSkillProfile] = useState(null);
  const [lastEvaluation, setLastEvaluation] = useState(null);
  
  // Evaluation workflow states
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [selfEvalDone, setSelfEvalDone] = useState(false);
  const [managerEvalDone, setManagerEvalDone] = useState(false);
  const [n2ValidationPending, setN2ValidationPending] = useState(false);
  const [evaluationStatus, setEvaluationStatus] = useState(null);
  
  // Stats dynamiques
  const [evaluationStats, setEvaluationStats] = useState({
    previousScore: null,
    currentScore: null,
    lastEvaluationDate: null,
    managerComment: null
  });
  
  useEffect(() => {
    fetchEmployeeData();
  }, [user?.id]);
  
  const fetchEmployeeData = async () => {
    if (!user?.id || !user?.companyId) return;
    
    setLoading(true);
    try {
      // 1. Récupérer l'employé lié à cet utilisateur
      const myEmployeeRes = await api.get(`/employee-accounts/my-employee?user_id=${user.id}&company_id=${user.companyId}`);
      
      if (!myEmployeeRes.data.found) {
        setNotLinked(true);
        setLoading(false);
        return;
      }
      
      const myEmployee = myEmployeeRes.data.employee;
      const jobProfile = myEmployeeRes.data.jobProfile;
      const departmentName = myEmployeeRes.data.departmentName;
      
      // 2. Récupérer le manager si existe
      let manager = null;
      if (myEmployee.managerId) {
        const allEmployeesRes = await api.get(`/employees?companyId=${user.companyId}`).catch(() => ({ data: [] }));
        const employees = Array.isArray(allEmployeesRes.data) ? allEmployeesRes.data : allEmployeesRes.data.employees || [];
        manager = employees.find(e => e.id === myEmployee.managerId);
      }
      
      setEmployeeData({
        ...myEmployee,
        manager,
        jobProfile,
        departmentName: departmentName || myEmployee.department
      });
      
      // 3. Récupérer données en parallèle
      const [objectivesRes, skillsRes, campaignsRes, plansRes, performanceEvalsRes] = await Promise.all([
        api.get(`/objectives`).catch(() => ({ data: { objectives: [] } })),
        api.get(`/skills-intelligence/employee/${myEmployee.id}?company_id=${user.companyId}`).catch(() => ({ data: {} })),
        api.get(`/performance-eval/campaigns`).catch(() => ({ data: { campaigns: [] } })),
        api.get(`/skills-intelligence/upskilling-plans?company_id=${user.companyId}&employee_id=${myEmployee.id}`).catch(() => ({ data: { plans: [] } })),
        api.get(`/performance-eval/my-evaluations?employee_id=${myEmployee.id}`).catch(() => ({ data: { evaluations: [] } }))
      ]);
      
      // Get performance evaluations for this employee
      const performanceEvals = performanceEvalsRes.data?.evaluations || [];
      // Find any ongoing evaluation (not completed/validated yet)
      const activePerformanceEval = performanceEvals.find(e => 
        e.status === 'self_eval_pending' || 
        e.status === 'manager_eval_pending' ||
        e.status === 'n2_validation_pending' ||
        e.status === 'manager_eval_done'
      );
      
      // 4. Objectifs - priorité aux évaluations de performance actives
      const objData = objectivesRes.data?.objectives || [];
      let myObjectives = objData.filter(o => o.employeeId === myEmployee.id);
      
      // Si une évaluation de performance active existe, utiliser ses objectifs
      if (activePerformanceEval?.objectives?.length > 0) {
        myObjectives = activePerformanceEval.objectives.map((obj, idx) => ({
          id: obj.id || `perf_obj_${idx}`,
          title: obj.title,
          description: obj.description,
          progress: obj.selfScore || 0,
          status: obj.selfScore && obj.managerScore ? 'atteint' : 
                  obj.selfScore ? 'en_cours' : 'pending',
          weight: obj.weight,
          selfEvaluation: obj.selfScore ? { score: obj.selfScore, comment: obj.selfComment } : null,
          managerEvaluation: obj.managerScore ? { score: obj.managerScore, comment: obj.managerComment } : null,
          fromPerformanceEval: true,
          evaluationId: activePerformanceEval.id
        }));
      }
      // Sinon, utiliser ceux de la fiche de poste si aucun objectif individuel
      else if (myObjectives.length === 0 && jobProfile?.objectives?.length > 0) {
        myObjectives = jobProfile.objectives.map((obj, idx) => ({
          id: obj.id || `jp_obj_${idx}`,
          title: obj.title,
          description: obj.description,
          progress: 0,
          status: 'en_cours',
          weight: obj.weight,
          // SMART criteria
          specific: obj.specific,
          measurable: obj.measurable,
          achievable: obj.achievable,
          relevant: obj.relevant,
          timeBound: obj.timeBound,
          fromJobProfile: true
        }));
      }
      
      setObjectives(myObjectives.slice(0, 5).map(o => ({
        id: o.id,
        title: o.title,
        description: o.description,
        progress: o.selfEvaluation?.score || o.progress || 0,
        weight: o.weight,
        status: o.selfEvaluation && o.managerEvaluation ? 'atteint' : 
                o.selfEvaluation ? 'en_cours' : 
                o.status === 'rejected' ? 'en_retard' : 'pending',
        deadline: o.endDate || o.timeBound || o.deadline,
        selfEvalDone: !!o.selfEvaluation,
        managerEvalDone: !!o.managerEvaluation,
        selfScore: o.selfEvaluation?.score,
        managerScore: o.managerEvaluation?.score,
        fromJobProfile: o.fromJobProfile,
        fromPerformanceEval: o.fromPerformanceEval
      })));
      
      // 5. Compétences réelles depuis Skills Intelligence
      const skillProfileData = skillsRes.data?.skillProfile;
      if (skillProfileData?.skills?.length > 0) {
        setSkillProfile(skillProfileData);
        setSkills(skillProfileData.skills.slice(0, 6).map(s => ({
          name: s.skillName || s.name,
          level: s.currentLevel || 1,
          requiredLevel: s.requiredLevel || 3,
          gap: s.gap || 0,
          importance: s.importance
        })));
      } else {
        setSkills([]);
      }
      
      // 6. Campagne active et statut d'évaluation
      const campaigns = campaignsRes.data?.campaigns || [];
      const activeCamp = campaigns.find(c => c.status === 'active' || c.status === 'ongoing');
      
      // Use performance evaluation status if available
      if (activePerformanceEval) {
        const evaluatedCount = myObjectives.filter(o => o.selfEvaluation).length;
        // Self eval is done if status is past self_eval_pending
        const allSelfEvalDone = activePerformanceEval.status !== 'self_eval_pending';
        // Manager eval is done if status is past manager_eval_pending
        const allManagerEvalDone = ['manager_eval_done', 'n2_validation_pending', 'validated', 'completed'].includes(activePerformanceEval.status);
        // N2/RH validation is pending
        const isN2Pending = ['n2_validation_pending', 'manager_eval_done'].includes(activePerformanceEval.status);
        
        setSelfEvalDone(allSelfEvalDone);
        setManagerEvalDone(allManagerEvalDone);
        setN2ValidationPending(isN2Pending);
        setEvaluationStatus(activePerformanceEval.status);
      } else if (activeCamp) {
        setActiveCampaign(activeCamp);
        const hasSelfEval = myObjectives.some(o => o.selfEvaluation);
        const hasManagerEval = myObjectives.every(o => o.managerEvaluation);
        setSelfEvalDone(hasSelfEval);
        setManagerEvalDone(hasManagerEval);
      }
      
      if (activeCamp) {
        setActiveCampaign(activeCamp);
      }
      
      // 7. Actions en attente dynamiques
      const actions = [];
      const objsNeedingSelfEval = myObjectives.filter(o => !o.selfEvaluation && o.status !== 'rejected');
      
      if (objsNeedingSelfEval.length > 0) {
        actions.push({
          id: 'self_eval',
          type: 'auto_evaluation',
          title: `Auto-évaluer mes objectifs (${objsNeedingSelfEval.length})`,
          description: `${objsNeedingSelfEval.length} objectif(s) en attente de votre auto-évaluation`,
          priority: 'high',
          deadline: activeCamp?.endDate,
          path: '/my-evaluations'
        });
      }
      
      // 7b. Actions des plans de développement (upskilling)
      const plans = plansRes.data?.plans || [];
      const activePlans = plans.filter(p => p.status === 'active');
      
      activePlans.forEach(plan => {
        const pendingPlanActions = (plan.actions || []).filter(a => 
          a.status === 'pending' || !a.status
        );
        
        pendingPlanActions.forEach((action, idx) => {
          actions.push({
            id: `plan_${plan.id}_action_${idx}`,
            type: 'complete_skills',
            title: action.title || `Action ${idx + 1}`,
            description: `Plan: ${plan.skillName || 'Compétence'} - ${action.type === 'formation' ? 'Formation' : action.type === 'coaching' ? 'Coaching' : action.type === 'practice' ? 'Pratique' : 'Action'}`,
            priority: 'medium',
            deadline: action.deadline,
            path: '/my-evaluations',
            planId: plan.id,
            actionIndex: idx
          });
        });
      });
      
      setPendingActions(actions);
      
      // 8. Statistiques d'évaluation
      const completedObjectives = myObjectives.filter(o => o.selfEvaluation && o.managerEvaluation);
      if (completedObjectives.length > 0) {
        // Calculer le score moyen des évaluations complètes
        const avgManagerScore = completedObjectives.reduce((sum, o) => 
          sum + (o.managerEvaluation?.score || 0), 0) / completedObjectives.length;
        
        // Trouver la dernière évaluation
        const lastEval = completedObjectives.sort((a, b) => 
          new Date(b.managerEvaluation?.evaluatedAt || 0) - new Date(a.managerEvaluation?.evaluatedAt || 0)
        )[0];
        
        setEvaluationStats({
          currentScore: Math.round((avgManagerScore / 100) * 100),
          previousScore: null, // À implémenter avec l'historique
          lastEvaluationDate: lastEval?.managerEvaluation?.evaluatedAt,
          managerComment: lastEval?.managerEvaluation?.comment
        });
      }
      
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleAction = (action) => {
    if (action.path) {
      navigate(action.path);
    }
  };
  
  const getInitials = () => {
    if (employeeData) {
      return `${employeeData.firstName?.[0] || ''}${employeeData.lastName?.[0] || ''}`.toUpperCase();
    }
    return `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  };
  
  const calculateStatus = () => {
    if (pendingActions.some(a => a.priority === 'high')) return 'action_requise';
    if (objectives.some(o => o.status === 'en_retard')) return 'a_ameliorer';
    return 'en_progression';
  };
  
  const calculateScore = () => {
    // Utiliser le score du profil de compétences s'il existe
    if (skillProfile?.overallScore) {
      return Math.round(skillProfile.overallScore);
    }
    // Sinon calculer à partir des compétences
    if (skills.length === 0) return 0;
    const avgRatio = skills.reduce((sum, s) => {
      const required = s.requiredLevel || 3;
      const ratio = Math.min(s.level / required, 1);
      return sum + ratio;
    }, 0) / skills.length;
    return Math.round(avgRatio * 100);
  };
  
  const getScoreLevel = (score) => {
    if (score >= 90) return 'Expert';
    if (score >= 75) return 'Avancé';
    if (score >= 50) return 'Confirmé';
    return 'Débutant';
  };
  
  // Attendre que le plan soit chargé avant de prendre une décision
  if (planLoading) {
    return (
      <DashboardLayout title="Mon espace">
        <div className="space-y-6 animate-pulse">
          <div className="h-32 bg-slate-200 rounded-xl"></div>
          <div className="h-48 bg-slate-200 rounded-xl"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 bg-slate-200 rounded-xl"></div>
            <div className="h-64 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  // Si l'entreprise est FREE et l'utilisateur est un employé, bloquer l'accès
  // (Normalement, les employés FREE ne devraient pas pouvoir se connecter du tout)
  if (isFree && user?.role === 'Employé') {
    return (
      <DashboardLayout title="Mon espace">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Accès non disponible
          </h2>
          <p className="text-slate-600 text-center max-w-md mb-6">
            Le dashboard employé est une fonctionnalité PRO. 
            Votre entreprise utilise actuellement le plan gratuit.
          </p>
          <p className="text-sm text-slate-500 text-center max-w-md">
            Contactez votre administrateur RH pour plus d&apos;informations 
            ou pour passer au plan PRO.
          </p>
        </div>
      </DashboardLayout>
    );
  }
  
  // Si l'utilisateur n'est pas lié à un employé
  if (notLinked) {
    return (
      <DashboardLayout title="Mon espace">
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Compte non lié
          </h2>
          <p className="text-slate-600 text-center max-w-md mb-6">
            Votre compte utilisateur n&apos;est pas encore lié à votre fiche employé. 
            Contactez votre responsable RH pour activer votre accès.
          </p>
          <div className="bg-white rounded-lg p-4 max-w-sm mx-auto border">
            <p className="text-sm text-slate-500">Connecté en tant que :</p>
            <p className="font-medium text-slate-900">{user?.email}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (loading) {
    return (
      <DashboardLayout title="Mon espace">
        <div className="space-y-6 animate-pulse">
          <div className="h-32 bg-slate-200 rounded-xl"></div>
          <div className="h-48 bg-slate-200 rounded-xl"></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="h-64 bg-slate-200 rounded-xl"></div>
            <div className="h-64 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const score = calculateScore();
  const status = calculateStatus();
  
  return (
    <DashboardLayout title="Mon espace">
      <div className="space-y-6 max-w-5xl mx-auto">
        
        {/* 1️⃣ Header - Identité & Contexte */}
        <Card className="bg-gradient-to-br from-white to-blue-50/30 border-blue-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <Avatar className="h-20 w-20 border-4 border-white shadow-lg">
                <AvatarImage src={employeeData?.photoUrl} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-blue-700 text-white text-xl font-semibold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900">
                    {employeeData?.firstName || user?.firstName} {employeeData?.lastName || user?.lastName}
                  </h1>
                  <StatusBadge status={status} />
                </div>
                
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4 text-slate-400" />
                    {employeeData?.jobTitle || 'Poste non défini'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {employeeData?.departmentName || employeeData?.department || 'Département non défini'}
                  </span>
                  {employeeData?.manager && (
                    <span className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-slate-400" />
                      Manager: {employeeData.manager.firstName} {employeeData.manager.lastName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* 2️⃣ Bandeau Évaluation en cours (si campagne active) */}
        {activeCampaign && (
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <Star className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Évaluation en cours</h3>
                    <p className="text-blue-100">{activeCampaign.name}</p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-blue-200">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        Échéance: {activeCampaign.endDate ? new Date(activeCampaign.endDate).toLocaleDateString('fr-FR') : 'Non définie'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  {/* Workflow steps */}
                  <div className="hidden md:flex items-center gap-2">
                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                      !selfEvalDone ? 'bg-white text-blue-600' : 'bg-white/20 text-white'
                    }`}>
                      {selfEvalDone ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      Auto-éval
                    </div>
                    <div className="w-6 h-0.5 bg-white/30" />
                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                      managerEvalDone ? 'bg-white/20 text-white' : 
                      selfEvalDone ? 'bg-white text-blue-600' : 'bg-white/10 text-blue-200'
                    }`}>
                      {managerEvalDone ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      Manager
                    </div>
                    <div className="w-6 h-0.5 bg-white/30" />
                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                      evaluationStatus === 'validated' || evaluationStatus === 'completed' ? 'bg-white/20 text-white' :
                      n2ValidationPending ? 'bg-white text-blue-600' : 'bg-white/10 text-blue-200'
                    }`}>
                      {evaluationStatus === 'validated' || evaluationStatus === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      Validation RH
                    </div>
                    <div className="w-6 h-0.5 bg-white/30" />
                    <div className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                      evaluationStatus === 'completed' ? 'bg-white/20 text-white' : 'bg-white/10 text-blue-200'
                    }`}>
                      {evaluationStatus === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                      Synthèse
                    </div>
                  </div>
                  
                  <Link to="/my-evaluations">
                    <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-md">
                      {!selfEvalDone ? (
                        <>M&apos;auto-évaluer<ArrowRight className="w-4 h-4 ml-2" /></>
                      ) : n2ValidationPending ? (
                        <>En attente validation RH<Clock className="w-4 h-4 ml-2" /></>
                      ) : managerEvalDone ? (
                        <>Voir mon bilan<ArrowRight className="w-4 h-4 ml-2" /></>
                      ) : (
                        <>En attente du manager<Clock className="w-4 h-4 ml-2" /></>
                      )}
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="mt-4">
                {(() => {
                  // Calculate progress based on evaluation status
                  // 4 steps: self_eval (25%), manager_eval (50%), n2_validation (75%), completed (100%)
                  let totalProgress = 0;
                  if (evaluationStatus === 'completed') {
                    totalProgress = 100;
                  } else if (evaluationStatus === 'validated') {
                    totalProgress = 90;
                  } else if (['n2_validation_pending', 'manager_eval_done'].includes(evaluationStatus)) {
                    totalProgress = 75;
                  } else if (evaluationStatus === 'manager_eval_pending') {
                    totalProgress = 50;
                  } else if (selfEvalDone) {
                    totalProgress = 50;
                  } else {
                    // Partial self-eval progress
                    const totalObjs = objectives.length || 1;
                    const selfEvaluatedCount = objectives.filter(o => o.selfEvalDone).length;
                    totalProgress = Math.round((selfEvaluatedCount / totalObjs) * 50);
                  }
                  return (
                    <>
                      <div className="flex justify-between text-xs text-blue-200 mb-1">
                        <span>Progression de votre évaluation</span>
                        <span>{totalProgress}%</span>
                      </div>
                      <Progress 
                        value={totalProgress} 
                        className="h-2 bg-white/20" 
                      />
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* 3️⃣ Carte centrale - Ma situation aujourd'hui */}
        <Card className="border-2 border-blue-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-500" />
              Ma situation aujourd&apos;hui
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Score */}
              <div className="text-center">
                <div className="relative inline-flex items-center justify-center w-28 h-28">
                  <svg className="w-full h-full -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="8"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      fill="none"
                      stroke={score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${(score / 100) * 314} 314`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-slate-900">{score}</span>
                    <span className="text-xs text-slate-500">/ 100</span>
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium text-slate-700">Score global</p>
                <Badge variant="outline" className="mt-1">{getScoreLevel(score)}</Badge>
              </div>
              
              {/* Évolution */}
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-2">
                  {skillProfile?.overallScore >= 60 ? (
                    <>
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="text-lg font-semibold text-green-600">{Math.round(skillProfile?.overallScore || 0)}%</span>
                      <span className="text-sm text-slate-500">score compétences</span>
                    </>
                  ) : skillProfile?.overallScore ? (
                    <>
                      <TrendingDown className="w-5 h-5 text-amber-600" />
                      <span className="text-lg font-semibold text-amber-600">{Math.round(skillProfile?.overallScore || 0)}%</span>
                      <span className="text-sm text-slate-500">score compétences</span>
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-5 h-5 text-slate-400" />
                      <span className="text-sm text-slate-500">Pas encore évalué</span>
                    </>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Objectifs évalués</span>
                    <span className="font-medium">{objectives.filter(o => o.selfEvalDone).length}/{objectives.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Compétences au niveau</span>
                    <span className="font-medium">{skills.filter(s => s.level >= (s.requiredLevel || 3)).length}/{skills.length}</span>
                  </div>
                  {skillProfile?.criticalGapsCount > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Écarts critiques</span>
                      <span className="font-medium">{skillProfile.criticalGapsCount}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Message IA */}
              <div className={`p-4 rounded-lg ${isPro ? 'bg-gradient-to-br from-purple-50 to-blue-50' : 'bg-slate-50'}`}>
                {isPro ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-purple-500" />
                      <span className="text-xs font-medium text-purple-600">Analyse IA</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      Votre performance est stable. Votre principal axe d&apos;amélioration concerne 
                      <span className="font-medium text-purple-700"> l&apos;initiative</span>. 
                      Continuez sur cette lancée !
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">
                    Vous progressez bien ! Consultez vos objectifs pour identifier vos prochaines étapes.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* 3️⃣ Carte Actions en attente */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Mes actions en attente
                {pendingActions.length > 0 && (
                  <Badge className="bg-amber-100 text-amber-700 ml-2">{pendingActions.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingActions.length > 0 ? (
                <div className="space-y-3">
                  {pendingActions.map((action) => (
                    <ActionCard key={action.id} action={action} onAction={handleAction} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p className="font-medium">Aucune action en attente</p>
                  <p className="text-sm mt-1">Continuez votre bon travail</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 4️⃣ Carte Mes objectifs */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-500" />
                  Mes objectifs
                </CardTitle>
                <Link to="/my-evaluations">
                  <Button variant="ghost" size="sm">
                    Voir tout
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {objectives.length > 0 ? (
                <div className="space-y-3">
                  {objectives.slice(0, 3).map((objective) => (
                    <ObjectiveCard key={objective.id} objective={objective} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Aucun objectif défini</p>
                  <p className="text-sm mt-1">Vos objectifs apparaîtront ici</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* 5️⃣ Carte Mes compétences */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-500" />
                  Mes compétences clés
                </CardTitle>
                <Link to="/my-skills">
                  <Button variant="ghost" size="sm">
                    Voir tout
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {skills.length > 0 ? (
                <div className="flex flex-col items-center">
                  <SimpleRadarChart skills={skills} />
                  
                  <div className="mt-4 flex flex-wrap gap-2 justify-center">
                    {skills.map((skill, i) => {
                      const isAtLevel = skill.level >= (skill.requiredLevel || 3);
                      const isCritical = skill.importance === 'critical';
                      return (
                        <span 
                          key={i}
                          className={`text-xs px-2 py-1 rounded-full ${
                            isAtLevel ? 'bg-green-100 text-green-700' :
                            isCritical ? 'bg-red-100 text-red-700' :
                            skill.level >= 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}
                        >
                          {skill.name}: {skill.level}/{skill.requiredLevel || 5}
                          {isCritical && !isAtLevel && ' ⚠️'}
                        </span>
                      );
                    })}
                  </div>
                  
                  {isPro && skills.filter(s => s.gap > 0 && s.importance === 'critical').length > 0 && (
                    <p className="mt-4 text-xs text-center text-red-600 bg-red-50 rounded-lg p-2">
                      ⚠️ Vous avez <strong>{skills.filter(s => s.gap > 0 && s.importance === 'critical').length}</strong> compétence(s) critique(s) à développer
                    </p>
                  )}
                  
                  {isPro && skills.filter(s => s.gap > 0 && s.importance === 'critical').length === 0 && skills.filter(s => s.gap > 0).length > 0 && (
                    <p className="mt-4 text-xs text-center text-purple-600 bg-purple-50 rounded-lg p-2">
                      💡 Développez vos compétences pour améliorer votre score de préparation au poste
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Pas de profil de compétences</p>
                  <p className="text-sm mt-1">Votre profil de poste n&apos;a pas encore été configuré</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* 6️⃣ Carte Évolution & Reconnaissance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Mon évolution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Dernière évaluation */}
                {evaluationStats.currentScore !== null ? (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-500">Score d&apos;évaluation</span>
                      {evaluationStats.lastEvaluationDate && (
                        <span className="text-xs text-slate-400">
                          {new Date(evaluationStats.lastEvaluationDate).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className={`text-3xl font-bold ${evaluationStats.currentScore >= 70 ? 'text-green-600' : evaluationStats.currentScore >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {evaluationStats.currentScore}%
                        </p>
                        <p className="text-xs text-slate-500">Score manager</p>
                      </div>
                      {evaluationStats.currentScore >= 70 && (
                        <Badge className="bg-green-100 text-green-700">Satisfaisant</Badge>
                      )}
                      {evaluationStats.currentScore >= 50 && evaluationStats.currentScore < 70 && (
                        <Badge className="bg-amber-100 text-amber-700">À améliorer</Badge>
                      )}
                      {evaluationStats.currentScore < 50 && (
                        <Badge className="bg-red-100 text-red-700">Insuffisant</Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-500">Pas encore d&apos;évaluation complète</p>
                    <p className="text-xs text-slate-400 mt-1">Vos scores apparaîtront ici après évaluation</p>
                  </div>
                )}
                
                {/* Commentaire manager */}
                {evaluationStats.managerComment ? (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium mb-1">Commentaire manager</p>
                    <p className="text-sm text-slate-700 italic">
                      &ldquo;{evaluationStats.managerComment}&rdquo;
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-400 font-medium mb-1">Commentaire manager</p>
                    <p className="text-sm text-slate-400 italic">
                      Pas encore de commentaire
                    </p>
                  </div>
                )}
                
                {/* Badges dynamiques */}
                <div className="flex flex-wrap gap-2">
                  {objectives.filter(o => o.status === 'atteint').length > 0 && (
                    <Badge className="bg-green-100 text-green-700 border border-green-200">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      {objectives.filter(o => o.status === 'atteint').length} objectif(s) évalué(s)
                    </Badge>
                  )}
                  {skills.filter(s => s.level >= s.requiredLevel).length > 0 && (
                    <Badge className="bg-purple-100 text-purple-700 border border-purple-200">
                      <Star className="w-3 h-3 mr-1" />
                      {skills.filter(s => s.level >= s.requiredLevel).length} compétence(s) au niveau
                    </Badge>
                  )}
                  {skillProfile?.overallScore >= 70 && (
                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                      <Award className="w-3 h-3 mr-1" />
                      Performance globale
                    </Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
      </div>
    </DashboardLayout>
  );
}
